import { h, Fragment } from 'preact';
import { PureComponent } from 'preact/compat';
import { css } from 'react-emotion';
import { withContext } from '../../../common/context';
import { FlexCenterStretched } from '../../../utils/emotion';
import MediaSymbol from '../../MediaSymbol/MediaSymbol';
import { isMobile } from 'mobile-device-detect';
import { DisplayMode } from '../../../typing/enums';
import { VideoWrapper } from './NativeVideoPlayer.styled';
import { KeyboardKey } from '../../../typing/keyboardKey';
import Error from '../../Error/Error';

const progressEvent = () => {
  let counter = 0;

  return progress => {
    if (progress > counter) {
      counter++;

      return true;
    }

    return false;
  };
};

class NativeVideoPlayer extends PureComponent {
  static NATIVE_TOTAL_PROGRESS = 4;

  errorCounter = 0;

  componentDidMount() {
    if (this.props.active && !this.state.url) {
      this.init();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.video && this.props.isReady) {
      const isSameAspectRatio = this.props.getIsSameRatio(prevProps);

      const isSameTransformation = prevProps.transformation === this.props.transformation;

      this.props.shouldAutoPlay(prevProps, this);

      if (
        (!this.props.active && this.isSizeChanged(prevProps)) ||
        !isSameAspectRatio ||
        !isSameTransformation
      ) {
        this.props.onReady(isSameAspectRatio || isSameTransformation);
        this.init();
      }
    } else if (this.props.active && !this.state.url) {
      this.init();
    }
  }

  componentWillUnmount() {
    if (this.keyupContainer) {
      this.keyupContainer.removeEventListener('keyup', this.onKeyUp);
    }
  }

  init() {
    const { width, height, transformation, videoPlayerSource } = this.props;

    const url = this.props.context.cloudinary.getVideoUrl(
      this.props.publicId,
      width,
      height,
      transformation,
      videoPlayerSource
    );

    this.setState({
      url,
      key: url[0]
    });

    if (this.keyupContainer) {
      this.keyupContainer.removeEventListener('keyup', this.onKeyUp);
    }

    if (isMobile) {
      this.video.setAttribute('playsinline', '');
    }

    if (this.props.isControls) {
      const mode = this.props.context.config.selectDisplayPropsMode();
      if (mode === DisplayMode.CLASSIC) {
        // Classic - attach EventListener to gallery
        this.keyupContainer = this.props.context.config.selectContainer();
      } else {
        // Expanded - attach EventListener to asset (button)
        this.keyupContainer = this.video.closest('button');
      }

      this.keyupContainer.addEventListener('keyup', this.onKeyUp);
    }
  }

  onTimeUpdate = () => {
    if (this.video) {
      const { currentTime, duration } = this.video;
      const progress =
        Math.floor(
          ((currentTime / duration) * 100) / (100 / NativeVideoPlayer.NATIVE_TOTAL_PROGRESS)
        ) || 0;

      if (progress === 0 || !this.progressEvent) {
        this.progressEvent = progressEvent();
      }

      // video with loop:true will not fire the 4th 100% `videoprogress`
      this.progress =
        this.loopProp &&
        progress === 0 &&
        this.progress === NativeVideoPlayer.NATIVE_TOTAL_PROGRESS - 1
          ? NativeVideoPlayer.NATIVE_TOTAL_PROGRESS
          : progress;

      if (
        this.progressEvent(this.progress) &&
        this.progress <= NativeVideoPlayer.NATIVE_TOTAL_PROGRESS
      ) {
        this.props.onTimeProgress((progress * 100) / NativeVideoPlayer.NATIVE_TOTAL_PROGRESS);
      }
    }
  };

  onKeyUp = event => {
    if (
      event.key === KeyboardKey.Spacebar ||
      (event.key === ' ' && this.props.isReady && this.props.active)
    ) {
      this.toggle();
    }
  };

  onEnded = () => {
    const loopProp = this.props.context.config.selectVideoPropsLoop();

    if (this.props.isControls && !loopProp) {
      this.progressEvent = progressEvent();
      this.setState({ isPlay: false });
    }
  };

  onError = () => {
    this.errorCounter = this.errorCounter + 1;
    if (this.errorCounter === this.state.url.length) {
      this.setState({ error: true });
    }
  };

  onCanPlay = () => {
    this.props.onReady(true);
  };

  renderMediaSymbol = () => {
    const mediaSymbolProps = this.props.context.config.selectMediaSymbolProps();
    const { isOver, isPlay } = this.state;
    const isShow = !isPlay || (isPlay && isOver);

    return (
      <FlexCenterStretched
        absolute
        data-test="video-media-icon-wrap"
        className={css({
          opacity: isShow ? 1 : 0,
          transition: 'opacity .4s ease-in'
        })}
      >
        <MediaSymbol
          {...mediaSymbolProps}
          size={this.props.width / 6}
          icon={this.state.isPlay ? 'pause' : 'play'}
          data-test={this.state.isPlay ? 'video-pause' : 'video-play'}
        />
      </FlexCenterStretched>
    );
  };

  renderMinimalControls = () => {
    return (
      <FlexCenterStretched
        innerRef={elem => {
          this.trigger = elem;
        }}
        data-test="video-trigger"
        absolute
        onMouseOver={this.onMouseOver}
        onMouseOut={this.onMouseOut}
        onClick={this.toggle}
      />
    );
  };

  onMouseOver = () => {
    if (isMobile) return;

    this.setState({ isOver: true });

    this.mouseOverTimeout = setTimeout(() => {
      if (this.state.isOver) {
        this.setState({ isOver: false });
      }
    }, 5000);

    if (this.props.isControls) {
      document.body.style.cursor = 'pointer';
    }
  };

  onMouseOut = () => {
    if (isMobile) return;

    if (this.state.isOver) {
      this.setState({ isOver: false });
    }
    if (this.mouseOverTimeout) {
      clearTimeout(this.mouseOverTimeout);
    }

    if (this.props.isControls) {
      document.body.style.cursor = '';
    }
  };

  toggle = () => {
    this.state.isPlay ? this.stop() : this.play();
  };

  onPlay = () => {
    this.setState({ isPlay: true });
    this.props.onPlay();
  };

  onPause = () => {
    this.setState({ isPlay: false });
    this.props.onPause();
  };

  play = () => {
    if (!this.video.paused) {
      this.setState({ isPlay: true });
    } else {
      const playPromise = this.video.play();
      // https://developers.google.com/web/updates/2016/03/play-returns-promise
      // In browsers that don’t yet support this functionality,
      // playPromise won’t be defined.
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Automatic playback started!
            this.onPlay();
          })
          .catch(error => {
            this.setState({ isPlay: false });
            console.error('Video Error: Click to play!', error);
          });
      } else {
        this.onPlay();
      }
    }
  };

  stop = () => {
    if (!this.video.paused) {
      this.video.pause();
      this.onPause();
    }
  };

  isSizeChanged(prevProps) {
    return this.props.width > prevProps.width || this.props.height > prevProps.height;
  }

  render(props, state) {
    const isMinimalControls = props.isMinimalControls && props.isReady && !state.error;
    const isLoading = !props.isReady && !state.error && !props.isNativeControls;

    return (
      <Fragment>
        <VideoWrapper
          disablepictureinpicture
          controlslist="nodownload"
          controls={props.isNativeControls}
          itemprop="video"
          innerRef={elem => (this.video = elem)}
          width={props.width}
          height={props.height}
          muted={props.isMuted}
          key={state.key}
          loop={props.isLoop}
          autoplay={props.isAutoPlay}
          playsinline={isMobile}
          onCanPlay={this.onCanPlay}
          onTimeUpdate={this.onTimeUpdate}
          onEnded={this.onEnded}
          onMouseOver={this.onMouseOver}
          onMouseOut={this.onMouseOut}
          onPlay={this.props.isNativeControls ? this.onPlay : null}
          onPause={this.props.isNativeControls ? this.onPause : null}
          aria-label={props.ariaLabel}
          data-test="native-video-player"
        >
          {state.url?.map(url => (
            <source onError={this.onError} src={url} key={url} />
          ))}
        </VideoWrapper>
        {isLoading && props.loadingComponent()}
        {isMinimalControls && this.renderMediaSymbol()}
        {isMinimalControls && this.renderMinimalControls()}
        {state.error && (
          <FlexCenterStretched absolute>
            <Error width={props.width} />
          </FlexCenterStretched>
        )}
      </Fragment>
    );
  }
}

export default withContext(NativeVideoPlayer);
